
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api/axios";
import {
    Crown,
    Rocket,
    Building2,
    Users,
    Film,
    LayoutTemplate,
    Megaphone,
    Sparkles,
    Loader2,
    Star,
    CreditCard,
    Lock,
    CheckCircle,
    X,
    ArrowRight,
    MessageCircle,
    ShieldCheck,
    Headphones,
    Settings2,
} from "lucide-react";

import Footer from "../../layouts/Footer";
import { getUser } from "../../api/auth";
import AuthPopup from "../../components/popup/AuthPopup";

export default function SubscriptionPlansComponent({ role }) {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState("YEARLY");
    const [currentPlan, setCurrentPlan] = useState("FREE");

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // Payment form states
    const [paymentMethod, setPaymentMethod] = useState("card");
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvv, setCvv] = useState("");
    const [upiId, setUpiId] = useState("");
    const [bank, setBank] = useState("");
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [showAuth, setShowAuth] = useState(false);

    const navigate = useNavigate();
    const user = getUser();

    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        try {
            setLoading(true);

            const response = await api.get("/subscription/plans");

            if (response?.data?.success) {
                setPlans(response.data.data || []);
            } else {
                setPlans([]);
            }
        } catch (err) {
            console.error(err);
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // PLAN STYLES
    // ============================================================
    const getPlanStyles = (planKey) => {
        switch (planKey) {
            case "FREE":
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-gray-100 text-gray-600",
                    button: "bg-gray-800 hover:bg-gray-900",
                };

            case "STARTER":
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-blue-100 text-blue-600",
                    button: "bg-blue-600 hover:bg-blue-700",
                };

            case "PRO":
                return {
                    border: "border-gray-200",
                    bg: "bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600",
                    text: "text-white",
                    iconBg: "bg-white/20 text-white",
                    button: "bg-white text-indigo-700 hover:bg-indigo-50",
                    isPopular: true,
                };

            case "ENTERPRISE":
                return {
                    border: "border-emerald-200",
                    bg: "bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700",
                    text: "text-white",
                    iconBg: "bg-white/20 text-white",
                    button: "bg-white text-emerald-700 hover:bg-emerald-50",
                    isEnterprise: true,
                };

            default:
                return {
                    border: "border-gray-200",
                    bg: "bg-white",
                    text: "text-gray-900",
                    iconBg: "bg-gray-100 text-gray-600",
                    button: "bg-gray-800 hover:bg-gray-900",
                };
        }
    };

    // ============================================================
    // PLAN ICON
    // ============================================================
    const getPlanIcon = (planKey) => {
        switch (planKey) {
            case "FREE":
                return Crown;

            case "STARTER":
                return Star;

            case "PRO":
                return Rocket;

            case "ENTERPRISE":
                return Building2;

            default:
                return Crown;
        }
    };

    // ============================================================
    // FORMAT LIMIT
    // ============================================================
    const formatLimit = (value) => {
        return value >= 999999 ? "♾️ Unlimited" : value;
    };

    // ============================================================
    // HANDLE PLAN ACTION
    // ============================================================
    const handlePlanAction = (plan) => {
        // Enterprise does not use the payment popup.
        // Redirect directly to Contact page.
        if (plan.plan_key === "ENTERPRISE") {
            navigate("/contact");
            return;
        }

        openModal(plan);
    };

    // ============================================================
    // OPEN PAYMENT MODAL
    // ============================================================
    const openModal = (plan) => {
        if (!user) {
            setShowAuth(true);
            return;
        }

        setSelectedPlan(plan);
        setShowModal(true);
        setPaymentSuccess(false);
        setPaymentError("");

        // Reset form fields
        setCardNumber("");
        setExpiry("");
        setCvv("");
        setUpiId("");
        setBank("");
    };

    // ============================================================
    // CLOSE PAYMENT MODAL
    // ============================================================
    const closeModal = () => {
        setShowModal(false);
        setSelectedPlan(null);
    };

    // ============================================================
    // HANDLE PAYMENT
    // ============================================================
    const handlePayment = async (e) => {
        e.preventDefault();

        setPaymentError("");
        setLoadingPayment(true);

        try {
            // Simulate API call – replace with real payment integration
            await new Promise((resolve) => setTimeout(resolve, 2000));

            setPaymentSuccess(true);

            // Optionally update current plan here
            // setCurrentPlan(selectedPlan.plan_key);
        } catch (err) {
            setPaymentError("Payment failed. Please try again.");
        } finally {
            setLoadingPayment(false);
        }
    };

    // ============================================================
    // LOADING
    // ============================================================
    if (loading) {
        return (
            <div className="flex h-[70vh] items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">

            {/* ====================================================
                HERO SECTION
            ==================================================== */}
            <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
                <div className="relative mx-auto max-w-7xl px-6 py-24 text-center">
                    <div className="mx-auto max-w-3xl">

                        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm">
                            <Sparkles size={18} />
                            Flexible Pricing for Every Team
                        </div>

                        <h1 className="text-5xl font-extrabold tracking-tight sm:text-6xl">
                            Choose Your{" "}
                            <span className="text-indigo-300">
                                Perfect
                            </span>{" "}
                            Plan
                        </h1>

                        <p className="mt-6 text-lg text-slate-300">
                            Simple, transparent pricing that scales with your
                            business. Upgrade anytime – no hidden fees.
                        </p>
                    </div>
                </div>
            </section>

            {/* ====================================================
                PLANS
            ==================================================== */}
            <div className="mx-auto max-w-7xl px-6 py-12">

                {plans.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 py-24 backdrop-blur-sm">

                        <Crown
                            size={70}
                            className="mb-5 text-gray-300"
                        />

                        <h2 className="text-2xl font-semibold text-gray-700">
                            No Plans Available
                        </h2>

                        <p className="mt-2 max-w-md text-center text-gray-500">
                            We're currently updating our subscription plans.
                            Please check back later or contact support.
                        </p>
                    </div>
                ) : (

                    <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">

                        {plans.map((plan) => {
                            const styles = getPlanStyles(plan.plan_key);
                            const Icon = getPlanIcon(plan.plan_key);

                            const isCurrent =
                                currentPlan === plan.plan_key;

                            const isPopular =
                                styles.isPopular || false;

                            const isEnterprise =
                                plan.plan_key === "ENTERPRISE";

                            return (
                                <div
                                    key={plan.plan_id}
                                    className={`group relative flex flex-col overflow-hidden rounded-3xl border ${styles.border} bg-white/80 backdrop-blur-sm shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${isEnterprise
                                        ? "hover:shadow-emerald-100/60"
                                        : "hover:shadow-indigo-100/50"
                                        }`}
                                >

                                    {/* ====================================================
                                        ENTERPRISE GLOW
                                    ==================================================== */}
                                    {isEnterprise && (
                                        <>
                                            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-300/20 blur-3xl" />

                                            <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-cyan-300/20 blur-3xl" />
                                        </>
                                    )}

                                    {/* ====================================================
                                        POPULAR BADGE
                                    ==================================================== */}
                                    {isPopular && (
                                        <div className="absolute -right-12 top-10 rotate-45 bg-yellow-400 px-12 py-1 text-xs font-bold uppercase text-yellow-900 shadow-lg">
                                            Popular
                                        </div>
                                    )}

                                    {/* ====================================================
                                        CURRENT PLAN BADGE
                                    ==================================================== */}
                                    {isCurrent && (
                                        <div className="absolute right-5 top-5 rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white shadow-md">
                                            Current Plan
                                        </div>
                                    )}

                                    {/* ====================================================
                                        PLAN HEADER
                                    ==================================================== */}
                                    <div
                                        className={`relative p-6 ${styles.bg} ${styles.text} transition-colors duration-300`}
                                    >

                                        <div
                                            className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${styles.iconBg} transition-transform group-hover:scale-110`}
                                        >
                                            <Icon size={28} />
                                        </div>

                                        <h3 className="text-2xl font-bold">
                                            {plan.plan_name}
                                        </h3>

                                        <p className="mt-1 text-sm opacity-80">
                                            {isEnterprise
                                                ? "Tailored solutions for growing enterprises"
                                                : plan.plan_description}
                                        </p>

                                        {/* ====================================================
                                            PRICE / ENTERPRISE MESSAGE
                                        ==================================================== */}
                                        {isEnterprise ? (
                                            <div className="mt-6">

                                                <div className="flex items-center gap-2">
                                                    <MessageCircle
                                                        size={22}
                                                        className="text-white/80"
                                                    />

                                                    <span className="text-sm font-medium text-white/80">
                                                        Let's build the right
                                                        plan for you
                                                    </span>
                                                </div>

                                                <div className="mt-2 text-3xl font-extrabold tracking-tight">
                                                    Custom Pricing
                                                </div>

                                                <p className="mt-1 text-xs text-white/70">
                                                    Flexible pricing based on
                                                    your business needs
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="mt-6 flex items-end">
                                                <span className="text-5xl font-extrabold tracking-tight">
                                                    ₹{plan.plan_price}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* ====================================================
                                        ENTERPRISE SPECIAL MESSAGE
                                    ==================================================== */}
                                    {isEnterprise && (
                                        <div className="border-b border-emerald-100 bg-emerald-50/70 px-6 py-4">

                                            <div className="flex items-start gap-3">

                                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                                                    <Settings2 size={17} />
                                                </div>

                                                <div>
                                                    <p className="text-sm font-semibold text-emerald-900">
                                                        Built around your
                                                        business
                                                    </p>

                                                    <p className="mt-1 text-xs leading-5 text-emerald-700">
                                                        Need higher limits,
                                                        dedicated support or
                                                        custom integrations?
                                                        Our team can create a
                                                        plan specifically for
                                                        you.
                                                    </p>
                                                </div>

                                            </div>
                                        </div>
                                    )}

                                    {/* ====================================================
                                        FEATURES
                                    ==================================================== */}
                                    <div className="flex-1 p-6">

                                        {isEnterprise ? (
                                            <div className="flex h-full flex-col justify-center">


                                            </div>
                                        ) : (
                                            <div className="space-y-3">

                                                {[
                                                    {
                                                        icon: Users,
                                                        label: "Users",
                                                        value: plan.plan_limits.users,
                                                    },
                                                    {
                                                        icon: Building2,
                                                        label: "Brands",
                                                        value: plan.plan_limits.brands,
                                                    },
                                                    {
                                                        icon: Film,
                                                        label: "Creatives",
                                                        value: plan.plan_limits.creatives,
                                                    },
                                                    {
                                                        icon: Megaphone,
                                                        label: "Campaigns",
                                                        value: plan.plan_limits.campaigns,
                                                    },
                                                ].map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0 last:pb-0"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-indigo-500">
                                                                <item.icon size={18} />
                                                            </div>

                                                            <span className="text-sm font-medium text-gray-700">
                                                                {item.label}
                                                            </span>
                                                        </div>

                                                        <span className="text-sm font-bold text-gray-900">
                                                            {formatLimit(item.value)}
                                                        </span>
                                                    </div>
                                                ))}

                                            </div>
                                        )}

                                    </div>

                                    {/* ====================================================
                                        ACTION BUTTON
                                    ==================================================== */}
                                    <div className="border-t border-gray-100 p-6">

                                        {isEnterprise ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate("/contact")
                                                }
                                                className="group/contact flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-lg"
                                            >
                                                <MessageCircle
                                                    size={18}
                                                />

                                                Contact Us

                                                <ArrowRight
                                                    size={18}
                                                    className="transition-transform duration-200 group-hover/contact:translate-x-1"
                                                />
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={`w-full rounded-xl py-3 font-semibold transition-all duration-200 ${isCurrent
                                                    ? "cursor-default bg-green-600 text-white opacity-40"
                                                    : isPopular
                                                        ? "bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-lg"
                                                        : "bg-slate-800 text-white hover:bg-slate-900 hover:shadow-lg"
                                                    }`}
                                                disabled={isCurrent}
                                                onClick={() =>
                                                    !isCurrent &&
                                                    handlePlanAction(plan)
                                                }
                                            >
                                                {isCurrent
                                                    ? "Current Plan"
                                                    : "Upgrade Now →"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ====================================================
                ENTERPRISE CONTACT CTA
            ==================================================== */}
            {plans.some(
                (plan) => plan.plan_key === "ENTERPRISE"
            ) && (
                    <section className="px-6 pb-16">

                        <div className="mx-auto max-w-7xl">

                            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 shadow-xl">

                                {/* Decorative circles */}
                                <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />

                                <div className="pointer-events-none absolute -bottom-20 left-1/3 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />

                                <div className="relative grid items-center gap-8 px-7 py-10 sm:px-10 lg:grid-cols-[1fr_auto] lg:px-14 lg:py-12">

                                    {/* Message */}
                                    <div>

                                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-indigo-200">
                                            <Sparkles size={14} />
                                            Enterprise Solutions
                                        </div>

                                        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                                            Looking for something more?
                                        </h2>

                                        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-300">
                                            Every business is different. Tell us
                                            what you need and our team will help
                                            create a subscription that fits your
                                            users, brands, creatives, campaigns,
                                            integrations and support requirements.
                                        </p>

                                        <div className="mt-6 flex flex-wrap gap-3">

                                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200">
                                                <CheckCircle
                                                    size={14}
                                                    className="text-emerald-300"
                                                />
                                                Custom limits
                                            </span>

                                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200">
                                                <CheckCircle
                                                    size={14}
                                                    className="text-emerald-300"
                                                />
                                                Priority support
                                            </span>

                                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200">
                                                <CheckCircle
                                                    size={14}
                                                    className="text-emerald-300"
                                                />
                                                Custom integrations
                                            </span>

                                        </div>
                                    </div>

                                    {/* CTA */}
                                    <div className="flex flex-col items-start lg:items-center">

                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate("/contact")
                                            }
                                            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 font-bold text-slate-900 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-indigo-50 hover:shadow-xl lg:w-auto"
                                        >
                                            <MessageCircle size={19} />

                                            Contact Us

                                            <ArrowRight
                                                size={18}
                                                className="transition-transform duration-200 group-hover:translate-x-1"
                                            />
                                        </button>

                                        <span className="mt-3 text-xs text-slate-500">
                                            Let's discuss your requirements
                                        </span>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

            {/* ====================================================
                PAYMENT POPUP MODAL
            ==================================================== */}
            {showModal && selectedPlan && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all"
                    onClick={closeModal}
                >
                    <div
                        className="relative mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* Close button */}
                        <button
                            type="button"
                            onClick={closeModal}
                            className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
                        >
                            <X size={20} />
                        </button>

                        {/* ====================================================
                            PAYMENT SUCCESS
                        ==================================================== */}
                        {paymentSuccess ? (
                            <div className="p-8 text-center">

                                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                                    <CheckCircle className="h-12 w-12 text-green-600" />
                                </div>

                                <h2 className="text-2xl font-bold text-gray-900">
                                    Payment Successful!
                                </h2>

                                <p className="mt-2 text-gray-600">
                                    Your subscription to{" "}
                                    <strong>
                                        {selectedPlan.plan_name}
                                    </strong>{" "}
                                    is now active.
                                </p>

                                <button
                                    type="button"
                                    onClick={() => {
                                        closeModal();

                                        // Optionally update current plan
                                        // setCurrentPlan(selectedPlan.plan_key);
                                    }}
                                    className="mt-6 rounded-xl bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (

                            /* ====================================================
                               PAYMENT FORM
                            ==================================================== */
                            <div className="grid gap-8 p-6 md:grid-cols-5">

                                {/* ====================================================
                                    LEFT: PLAN SUMMARY
                                ==================================================== */}
                                <div className="md:col-span-2">

                                    <h3 className="text-lg font-semibold">
                                        Selected Plan
                                    </h3>

                                    <div className="mt-4">

                                        <p className="text-2xl font-bold">
                                            {selectedPlan.plan_name}
                                        </p>

                                        <p className="text-sm text-gray-500">
                                            {selectedPlan.plan_description}
                                        </p>

                                        <p className="mt-2 text-3xl font-extrabold text-indigo-600">
                                            ₹{selectedPlan.plan_price}
                                        </p>
                                    </div>

                                    <hr className="my-4" />

                                    <ul className="space-y-2 text-sm">

                                        {Object.entries(
                                            selectedPlan.plan_limits
                                        ).map(([key, value]) => (
                                            <li
                                                key={key}
                                                className="flex justify-between"
                                            >
                                                <span className="capitalize text-gray-600">
                                                    {key}
                                                </span>

                                                <span className="font-medium">
                                                    {value >= 999999
                                                        ? "Unlimited"
                                                        : value}
                                                </span>
                                            </li>
                                        ))}

                                    </ul>
                                </div>

                                {/* ====================================================
                                    RIGHT: PAYMENT FORM
                                ==================================================== */}
                                <div className="md:col-span-3">

                                    <h3 className="text-lg font-semibold">
                                        Payment Details
                                    </h3>

                                    {/* Payment Method Selector */}
                                    <div className="mt-4 grid grid-cols-3 gap-2">

                                        {[
                                            "card",
                                            "upi",
                                            "netbanking",
                                        ].map((method) => (
                                            <button
                                                type="button"
                                                key={method}
                                                className={`rounded-lg border p-3 text-sm font-medium capitalize transition ${paymentMethod === method
                                                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                                    : "border-gray-200 hover:bg-gray-50"
                                                    }`}
                                                onClick={() =>
                                                    setPaymentMethod(method)
                                                }
                                            >
                                                {method === "card" && (
                                                    <CreditCard
                                                        className="mr-1 inline"
                                                        size={16}
                                                    />
                                                )}

                                                {method}
                                            </button>
                                        ))}

                                    </div>

                                    <form
                                        onSubmit={handlePayment}
                                        className="mt-6 space-y-4"
                                    >

                                        {/* CARD */}
                                        {paymentMethod === "card" && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Card Number
                                                    </label>

                                                    <input
                                                        type="text"
                                                        placeholder="1234 5678 9012 3456"
                                                        value={cardNumber}
                                                        onChange={(e) =>
                                                            setCardNumber(
                                                                e.target.value
                                                            )
                                                        }
                                                        className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                        required
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Expiry
                                                        </label>

                                                        <input
                                                            type="text"
                                                            placeholder="MM/YY"
                                                            value={expiry}
                                                            onChange={(e) =>
                                                                setExpiry(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            CVV
                                                        </label>

                                                        <input
                                                            type="password"
                                                            placeholder="•••"
                                                            value={cvv}
                                                            onChange={(e) =>
                                                                setCvv(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                            required
                                                        />
                                                    </div>

                                                </div>
                                            </>
                                        )}

                                        {/* UPI */}
                                        {paymentMethod === "upi" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    UPI ID
                                                </label>

                                                <input
                                                    type="text"
                                                    placeholder="example@upi"
                                                    value={upiId}
                                                    onChange={(e) =>
                                                        setUpiId(
                                                            e.target.value
                                                        )
                                                    }
                                                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                    required
                                                />
                                            </div>
                                        )}

                                        {/* NET BANKING */}
                                        {paymentMethod === "netbanking" && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Select Bank
                                                </label>

                                                <select
                                                    value={bank}
                                                    onChange={(e) =>
                                                        setBank(e.target.value)
                                                    }
                                                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
                                                    required
                                                >
                                                    <option value="">
                                                        Choose your bank
                                                    </option>

                                                    <option value="sbi">
                                                        State Bank of India
                                                    </option>

                                                    <option value="hdfc">
                                                        HDFC Bank
                                                    </option>

                                                    <option value="icici">
                                                        ICICI Bank
                                                    </option>

                                                    <option value="axis">
                                                        Axis Bank
                                                    </option>
                                                </select>
                                            </div>
                                        )}

                                        {/* PAYMENT ERROR */}
                                        {paymentError && (
                                            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                                                {paymentError}
                                            </div>
                                        )}

                                        {/* PAYMENT ACTION */}
                                        <div className="flex items-center justify-between pt-4">

                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <Lock size={16} />
                                                <span>
                                                    Secure payment
                                                </span>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={loadingPayment}
                                                className="rounded-xl bg-indigo-600 px-8 py-3 font-semibold text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-70"
                                            >
                                                {loadingPayment ? (
                                                    <span className="flex items-center gap-2">
                                                        <Loader2
                                                            className="animate-spin"
                                                            size={18}
                                                        />
                                                        Processing…
                                                    </span>
                                                ) : (
                                                    `Pay ₹${selectedPlan.plan_price}`
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ====================================================
                AUTH POPUP
            ==================================================== */}
            <AuthPopup
                isOpen={showAuth}
                onClose={() => setShowAuth(false)}
            />

            {/* ====================================================
                FOOTER
            ==================================================== */}
            {role === "open" && <Footer />}
        </div>
    );
}

